import { useTranslation } from 'next-i18next'

import CheckmarkIcon from '~/components/Icons/svgs/CheckmarkIcon'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import curriculumAnalytics from '~/analytics/curriculum/curriculumAnalytics'

interface Props {
  isAcknowledging?: boolean
  onConfirm: () => void
}

const featureKeys = ['easyAccess', 'smarterMind', 'newStats'] as const

export default function SystemUpdateModal({ isAcknowledging = false, onConfirm }: Props): React.ReactElement {
  const { t } = useTranslation('systemUpdate')

  const handleConfirm = (): void => {
    curriculumAnalytics.logSystemUpdateConfirmed()
    onConfirm()
  }

  return (
    <aside className="fixed inset-0 z-top bg-black/80 backdrop-blur-sm">
      <section className="flex min-h-full items-center justify-center p-3 md:p-4">
        <div className="my-0 flex w-full max-w-222 max-h-screen-off-15 flex-col overflow-y-auto rounded-3 border border-white/10 bg-gradient-purple-dark shadow-purple2 md:my-8 md:flex-row">
          <div className="relative flex flex-col items-center justify-center gap-4 bg-purple-darker px-5 py-6 text-center md:max-w-92.5 md:gap-10 md:px-8">
            <BrainStatic />
            <div className="space-y-1.5 md:space-y-2 md:self-stretch md:text-left">
              <h3 className="text-xl font-bold leading-7 text-purple-lavender md:text-2xl md:leading-8">
                {t('evolution.title')}
              </h3>
              <p className="text-xs leading-5 text-gray-lavender md:text-sm md:leading-5.5">{t('evolution.tagline')}</p>
            </div>
          </div>

          <div className="flex flex-col items-start px-5 py-6 text-left text-white md:w-130 md:px-8 md:py-12">
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-green-light-deep/10 px-3 py-1 text-xxs font-extrabold uppercase tracking-px text-green-light-deep">
                {t('badge')}
              </span>
              <span aria-hidden className="block size-1.5 rounded-full bg-pink-hot" />
              <span className="text-xs font-bold text-gray-400">{t('version')}</span>
            </div>

            <h2 className="mt-3 text-2xl font-black leading-tight md:mt-4 md:text-3xl md:leading-10 lg:text-4xl">
              <span className="block text-purple-lavender">{t('title')}</span>
              <span className="block text-blue-pastel">{t('titleHighlight')}</span>
            </h2>

            <ul className="mt-4 w-full space-y-4 md:mt-6 md:space-y-6">
              {featureKeys.map((key) => (
                <li className="flex items-start gap-3 md:gap-5" key={key}>
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-3 bg-purple-charcoal md:size-12">
                    <SVGIcon icon={CheckmarkIcon} pathClassName="text-white" size={18} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold text-white">{t(`features.${key}.title`)}</span>
                    <span className="block text-xs leading-snug text-white/90">{t(`features.${key}.description`)}</span>
                  </span>
                </li>
              ))}
            </ul>

            <button
              className="mt-6 flex h-12 w-full shrink-0 items-center justify-center rounded-9 border border-white bg-gradient-purple-violet px-4 text-sm font-black text-white shadow-black-bottom-right transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 md:mt-8 md:h-13 md:px-6 md:text-base lg:text-lg"
              disabled={isAcknowledging}
              onClick={handleConfirm}
              type="button"
            >
              {t('cta.confirm')}
            </button>
          </div>
        </div>
      </section>
    </aside>
  )
}

const SPARKLES = [
  { x: -19, y: -140, size: 40, opacity: 0.9 },
  { x: 94, y: -108, size: 40, opacity: 0.79 },
  { x: -121, y: -84, size: 39, opacity: 0.64 },
  { x: 150, y: -6, size: 38, opacity: 0.7 },
  { x: -154, y: 28, size: 36, opacity: 0.65 },
  { x: 118, y: 106, size: 35, opacity: 0.65 },
  { x: -97, y: 131, size: 34, opacity: 0.6 },
  { x: 16, y: 163, size: 34, opacity: 0.55 },
]

function SparkleOutline({ size }: { size: number }): React.ReactElement {
  return (
    <svg fill="none" height={size} viewBox="0 0 50 48" width={size} xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18.9286 25.0298C19.0821 24.5744 19.0803 24.0455 18.9234 23.4983C18.7663 22.9511 18.4599 22.4056 18.036 21.9186L10.5192 13.2855C10.3932 13.1355 10.3122 12.9658 10.2887 12.8021C10.2652 12.6385 10.3004 12.4898 10.3891 12.3786C10.4776 12.2675 10.6147 12.2 10.7795 12.1864C10.9443 12.1728 11.1277 12.2139 11.3022 12.3031L21.3952 17.704C21.9642 18.0086 22.5641 18.1858 23.1323 18.2169C23.7005 18.248 24.2164 18.1322 24.6262 17.8812L31.8903 13.4255C32.018 13.3518 32.1838 13.3293 32.3621 13.3614C32.5405 13.3934 32.722 13.4784 32.8786 13.6033C33.0352 13.7281 33.1584 13.8859 33.2295 14.0527C33.3006 14.2195 33.3156 14.3861 33.2723 14.527L30.5476 22.6007C30.3941 23.0563 30.3957 23.5851 30.5528 24.1323C30.7098 24.6794 31.0161 25.2248 31.4402 25.7119L38.9579 34.3439C39.0856 34.4943 39.1677 34.665 39.192 34.8297C39.2163 34.9944 39.1812 35.1441 39.0922 35.2559C39.003 35.3677 38.8649 35.4353 38.6989 35.4484C38.5329 35.4615 38.3483 35.4193 38.1733 35.3284L28.0818 29.9255C27.5125 29.6208 26.9124 29.4438 26.344 29.4128C25.7755 29.3818 25.2597 29.4981 24.85 29.7493L17.5859 34.2051C17.4582 34.2788 17.2924 34.3012 17.114 34.2692C16.9355 34.2371 16.7542 34.1521 16.5976 34.0273C16.441 33.9025 16.3176 33.7446 16.2466 33.5779C16.1756 33.4111 16.1605 33.2445 16.2038 33.1036L18.9286 25.0298Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.61186"
      />
    </svg>
  )
}

function BrainStatic(): React.ReactElement {
  return (
    <div
      aria-hidden
      className="relative flex size-24 origin-center scale-85 items-center justify-center overflow-visible text-purple-350 md:size-80 md:scale-100"
    >
      <div aria-hidden className="absolute inset-0 flex items-center justify-center scale-[0.50] md:scale-100">
        {SPARKLES.map((sparkle) => (
          <span
            className="absolute"
            key={`${sparkle.x}-${sparkle.y}`}
            style={{
              transform: `translate(${sparkle.x}px, ${sparkle.y}px)`,
              opacity: sparkle.opacity,
            }}
          >
            <SparkleOutline size={sparkle.size} />
          </span>
        ))}
      </div>
      <div className="relative z-10 h-20 w-21 drop-shadow-purple md:h-44 md:w-46">
        <ResponsiveImage
          height={796}
          imageClassName="size-full object-contain"
          imageWidths="180, 360, 540"
          loading="eager"
          srcKey="curriculum/mind_brain_ai.png"
          width={900}
        />
      </div>
    </div>
  )
}
